const tag = (arr, type) =>
  Array.isArray(arr) ? arr.map((b) => ({ ...b, __bookingType: type })) : [];

function nonEmptyString(v) {
  if (v == null) return "";
  const s = String(v).trim();
  return s;
}

/**
 * Same row shape as a normal block; only `managementBlock` differs on the API doc.
 * Accept common variants (boolean, number, string, alternate keys).
 */
function isManagementBlockRow(block) {
  if (!block || typeof block !== "object") return false;
  const v =
    block.managementBlock ??
    block.management_block ??
    block.isManagementBlock;
  if (v === true || v === 1) return true;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    return s === "true" || s === "1" || s === "yes";
  }
  return false;
}

/** Rows produced from `blockedDates` (normal + management). */
function isInventoryBlockedDateRow(b) {
  return (
    b &&
    (b.__bookingType === "blocked" || b.__bookingType === "managementBlock")
  );
}

/** Blocked-dates rows: normal vs management (aligned with dashboard tooltips). */
export function blockedRangeDisplayLabel(block) {
  const name = nonEmptyString(block.campaignName);
  if (isManagementBlockRow(block)) {
    return name ? `Blocked (management) — ${name}` : "Blocked (management)";
  }
  return name ? `Blocked — ${name}` : "Blocked";
}

/**
 * Turn populated blocked-date documents into booking-shaped ranges for availability.
 * Skips raw ObjectId refs and archived blocks. Any block with blockedAt/blockedTill
 * makes the inventory unavailable for that inclusive range (not only managementBlock).
 */
function blockedDatesToBookingRanges(blockedDates) {
  if (!Array.isArray(blockedDates)) return [];
  return blockedDates
    .filter(
      (block) =>
        block &&
        typeof block === "object" &&
        block.blockedAt != null &&
        block.blockedTill != null &&
        block.archived !== true,
    )
    .map((block) => {
      const startDate = new Date(block.blockedAt);
      const endDate = new Date(block.blockedTill);
      if (
        Number.isNaN(startDate.getTime()) ||
        Number.isNaN(endDate.getTime())
      ) {
        return null;
      }
      const management = isManagementBlockRow(block);
      const label = blockedRangeDisplayLabel({
        ...block,
        managementBlock: management,
      });
      return {
        ...block,
        startDate,
        endDate,
        // Same type as normal blocks so availability logic treats them identically;
        // use `managementBlock` / isManagementBlockRow() for labels and badges.
        __bookingType: "blocked",
        managementBlock: management,
        displayName: label,
      };
    })
    .filter(Boolean);
}

/**
 * Non-archived blocked-date docs merged like Bookings / FOC / premiumBlock:
 * - Full-site row: self + linked half1Id + half2Id.
 * - Half row: self + parent full's own `fullId.blockedDates` (blocks on the full
 *   inventory apply to both halves), same idea as `sliceBookingsAndPremium(fullId)`.
 * Deduplicated by `_id` when present.
 */
function collectInventoryBlockedDocuments(inventory) {
  if (!inventory) return [];
  const byKey = new Map();
  const add = (arr) => {
    if (!Array.isArray(arr)) return;
    for (const b of arr) {
      if (
        !b ||
        typeof b !== "object" ||
        b.blockedAt == null ||
        b.blockedTill == null ||
        b.archived === true
      ) {
        continue;
      }
      const key =
        b._id != null
          ? String(b._id)
          : `${b.blockedAt}-${b.blockedTill}-${isManagementBlockRow(b)}`;
      if (!byKey.has(key)) byKey.set(key, b);
    }
  };
  add(inventory.blockedDates);
  if (inventory.half1Id?._id) add(inventory.half1Id.blockedDates);
  if (inventory.half2Id?._id) add(inventory.half2Id.blockedDates);
  const linkedFull =
    inventory.fullId && typeof inventory.fullId === "object"
      ? inventory.fullId
      : null;
  // Inherit blocks stored on the parent full (half cards mirror full-site blocks).
  if (linkedFull) add(linkedFull.blockedDates);
  return [...byKey.values()];
}

function sliceBookingsAndPremium(invSlice) {
  if (!invSlice) return [];
  return [...tag(invSlice.Bookings, "normal"), ...tag(invSlice.premiumBlock, "normal")];
}

/**
 * Bookings + premium blocks from this inventory and linked half/full sites.
 * Same merge as dateAvailability (before blocked / FOC / rotational).
 */
export function getMergedCalendarBookings(inventory, seedBookings) {
  const primary = seedBookings ?? inventory?.Bookings;
  let list = tag(primary, "normal");
  if (!inventory) return list;
  if (inventory.half1Id?._id) {
    list = [...list, ...sliceBookingsAndPremium(inventory.half1Id)];
  }
  if (inventory.half2Id?._id) {
    list = [...list, ...sliceBookingsAndPremium(inventory.half2Id)];
  }
  if (inventory.premiumBlock?.length > 0) {
    list = [...list, ...tag(inventory.premiumBlock, "normal")];
  }
  if (inventory.fullId?._id) {
    list = [...list, ...sliceBookingsAndPremium(inventory.fullId)];
  }
  return list;
}

/** Blocked-date docs from this inventory and linked half/full (has range, not archived). */
export function getMergedBlockedDateDocuments(inventory) {
  return collectInventoryBlockedDocuments(inventory);
}

/** Premium-block entries from this inventory and linked half/full (calendar / parity). */
export function getMergedPremiumBlockEntries(inventory) {
  if (!inventory) return [];
  const out = [];
  const push = (arr) => {
    if (Array.isArray(arr)) out.push(...arr);
  };
  push(inventory.premiumBlock);
  if (inventory.half1Id?._id) push(inventory.half1Id.premiumBlock);
  if (inventory.half2Id?._id) push(inventory.half2Id.premiumBlock);
  if (inventory.fullId?._id) push(inventory.fullId.premiumBlock);
  return out;
}

/** FOC bookings on this inventory and linked half1 / half2 / full (same merge idea as Bookings). */
export function getMergedFocBookings(inventory) {
  if (!inventory) return [];
  const out = [];
  const add = (inv) => {
    if (inv && Array.isArray(inv.focBookings) && inv.focBookings.length > 0) {
      out.push(...tag(inv.focBookings, "foc"));
    }
  };
  add(inventory);
  if (inventory.half1Id?._id) add(inventory.half1Id);
  if (inventory.half2Id?._id) add(inventory.half2Id);
  if (inventory.fullId?._id) add(inventory.fullId);
  return out;
}

/** Rotational bookings on this inventory and linked half1 / half2 / full. */
export function getMergedRotationalBookings(inventory) {
  if (!inventory) return [];
  const out = [];
  const add = (inv) => {
    if (
      inv &&
      Array.isArray(inv.rotationalBookings) &&
      inv.rotationalBookings.length > 0
    ) {
      out.push(...tag(inv.rotationalBookings, "rotational"));
    }
  };
  add(inventory);
  if (inventory.half1Id?._id) add(inventory.half1Id);
  if (inventory.half2Id?._id) add(inventory.half2Id);
  if (inventory.fullId?._id) add(inventory.fullId);
  return out;
}

function dateAvailability(bookings, majorFeature, inventory) {


  // console.log("bookings:::: ", bookings);
  // console.log("inventory:::: ", inventory);

  bookings = getMergedCalendarBookings(inventory, bookings);

  bookings = [
    ...bookings,
    ...blockedDatesToBookingRanges(
      collectInventoryBlockedDocuments(inventory),
    ),
  ];

  // FOC / rotational: self + linked half1, half2, full (same as Bookings merge)
  bookings = [
    ...bookings,
    ...getMergedFocBookings(inventory),
    ...getMergedRotationalBookings(inventory),
  ];

  if (Array.isArray(bookings)) {
    const bookingDates = bookings.map((booking) => ({
      startDate: new Date(booking.startDate),
      endDate: new Date(booking.endDate),
    }));
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    bookingDates.sort((a, b) => a.endDate - b.endDate);

    let nextAvailableDate = new Date();
    nextAvailableDate.setHours(0, 0, 0, 0);
    let immediate = true;
    let indexForNextAvailability;
    let indexForDisplay;

    // Logic to handle majorFeature (if true)
    if (majorFeature) {
      bookings = bookings
        .slice()
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

      // Index for next availability
      bookings.forEach((ele, index) => {
        let startDate = new Date(ele.startDate);
        startDate.setHours(0, 0, 0, 0);
        if (currentDate.getTime() < startDate.getTime()) {
          indexForNextAvailability = index;
          return;
        }
      });

      // Index for present display: prefer management / inventory blocks over BDF etc.
      // (Sorted by startDate, a long booking can start before a block and hide it;
      // dashboard shows blocks in a separate calendar layer.)
      const coveringToday = [];
      bookings.forEach((ele, index) => {
        let startDate = new Date(ele.startDate);
        startDate.setHours(0, 0, 0, 0);
        let endDate = new Date(ele.endDate);
        endDate.setHours(0, 0, 0, 0);
        if (
          currentDate.getTime() >= startDate.getTime() &&
          currentDate.getTime() <= endDate.getTime()
        ) {
          coveringToday.push({ index, ele });
        }
      });
      const blockCovering = coveringToday.filter((x) =>
        isInventoryBlockedDateRow(x.ele),
      );
      const picked =
        blockCovering.find((x) => isManagementBlockRow(x.ele)) ||
        blockCovering[0] ||
        coveringToday[0];
      indexForDisplay = picked?.index;
    }

    // Fixing the loop logic for checking if next available date is correct
    for (const booking of bookingDates) {
      // Only consider future bookings or bookings that include today
      let bookingStartDate = new Date(booking.startDate);
      bookingStartDate.setHours(0, 0, 0, 0);
      let bookingEndDate = new Date(booking.endDate);
      bookingEndDate.setHours(0, 0, 0, 0);

      if (
        nextAvailableDate.getTime() >= bookingStartDate.getTime() &&
        currentDate.getTime() <= bookingEndDate.getTime()
      ) {
        nextAvailableDate = booking.endDate;
        nextAvailableDate.setDate(nextAvailableDate.getDate() + 1); // Set to the day after the current booking's endDate
        immediate = false; // Booking covers today, so it's not immediate
      }
    }

    // If no future bookings interfere, immediate is true.
    // We want to show the day after the most recent booking that has already ended.
    if (immediate) {
      // Find bookings that have already ended (end date is in the past)
      const pastBookings = bookingDates.filter(b => {
        const eDate = new Date(b.endDate);
        eDate.setHours(0, 0, 0, 0);
        return eDate.getTime() < currentDate.getTime();
      });

      if (pastBookings.length > 0) {
        // Find the most recent end date among PAST bookings
        const latestPastBookingDate = new Date(Math.max(...pastBookings.map(b => new Date(b.endDate).getTime())));
        latestPastBookingDate.setHours(0, 0, 0, 0);

        nextAvailableDate = new Date(latestPastBookingDate);
        nextAvailableDate.setDate(nextAvailableDate.getDate() + 1);
        nextAvailableDate.setHours(0, 0, 0, 0);
      } else {
        nextAvailableDate = new Date();
        nextAvailableDate.setHours(0, 0, 0, 0);
      }
    }

    // Return result based on majorFeature flag
    if (majorFeature) {
      const isMergedBlockEntry = (b) => isInventoryBlockedDateRow(b);

      return {
        status: immediate ? "IMMEDIATE" : "UP",
        nextAvailableDate,
        nextBooking:
          indexForNextAvailability === 0 || indexForNextAvailability
            ? (() => {
                const b = bookings[indexForNextAvailability];
                if (isMergedBlockEntry(b)) return b.displayName;
                return (
                  b?.bdfId?.brandId?.partyName ||
                  b?.premiumId?.parentCompanyId?.name ||
                  b?.campaignName ||
                  b?.displayName
                );
              })()
            : null,
        display:
          indexForDisplay === 0 || indexForDisplay
            ? (() => {
                const b = bookings[indexForDisplay];
                if (isMergedBlockEntry(b)) return b.displayName;
                return (
                  b?.bdfId?.campaignName ||
                  b?.bdfId?.brandId?.partyName ||
                  b?.bdfId?.brandId?.brandName ||
                  b?.premiumId?.campaignName ||
                  b?.premiumId?.parentCompanyId?.name ||
                  b?.campaignName ||
                  b?.displayName
                );
              })()
            : null,
        salesPerson:
          indexForDisplay === 0 || indexForDisplay
            ? bookings[indexForDisplay]?.bdfId?.createdBy?.name ||
            bookings[indexForDisplay]?.premiumId?.blockedBy?.name ||
            bookings[indexForDisplay]?.blockedBy?.name ||
            bookings[indexForDisplay]?.confirmedBy
            : null,
        currentBookingType:
          indexForDisplay === 0 || indexForDisplay
            ? (() => {
                const b = bookings[indexForDisplay];
                if (!b) return null;
                if (isInventoryBlockedDateRow(b)) {
                  return isManagementBlockRow(b)
                    ? "managementBlock"
                    : "blocked";
                }
                return b.__bookingType ?? null;
              })()
            : null,
      };
    } else {
      return {
        status: immediate ? "IMMEDIATE" : "UP",
        nextAvailableDate,
      };
    }
  }
}

export default dateAvailability;

// Example usage
//   let tempDates = [
//     {
//       startDate: "2024-06-01T00:00:00.000+00:00",
//       endDate: "2024-06-18T00:00:00.000+00:00"
//     },
//     {
//       startDate: "2024-06-19T00:00:00.000+00:00",
//       endDate: "2024-07-31T00:00:00.000+00:00"
//     },
//       {
//       startDate: "2024-08-01T00:00:00.000+00:00",
//       endDate: "2024-08-09T00:00:00.000+00:00"
//     },
//   ];

//   console.log(dateAvailability(tempDates));

//vanshita code
// function dateAvailability(bookings, majorFeature) {
//   if (Array.isArray(bookings)) {
//     const bookingDates = bookings.map(booking => ({
//       startDate: new Date(booking.startDate),
//       endDate: new Date(booking.endDate),
//     }));

//     // Find the latest end date
//     let latestEndDate = new Date(Math.max(...bookingDates.map(b => b.endDate)));

//     let nextAvailableDate = new Date(latestEndDate);
//     nextAvailableDate.setDate(nextAvailableDate.getDate() + 1); // Start after the last booking end date

//     let immediate = new Date() < latestEndDate;

//     let indexForNextAvailability;
//     let indexForDisplay;

//     // Logic for majorFeature
//     if (majorFeature) {
//       let sortedBookings = bookings.slice().sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

//       // Index for next availability
//       sortedBookings.forEach((ele, index) => {
//         if (new Date() <= new Date(ele.startDate)) {
//           indexForNextAvailability = index;
//           return;
//         }
//       });

//       // Index for Present Display
//       sortedBookings.forEach((ele, index) => {
//         if (new Date() >= new Date(ele.startDate) && new Date() <= new Date(ele.endDate)) {
//           indexForDisplay = index;
//           return;
//         }
//       });
//     }

//     return {
//       status: immediate ? "IMMEDIATE" : "UP",
//       nextAvailableDate,
//       ...(majorFeature && {
//         nextBooking: indexForNextAvailability !== undefined
//           ? bookings[indexForNextAvailability]?.bdfId?.brandId?.partyName
//           : null,
//         display: indexForDisplay !== undefined
//           ? bookings[indexForDisplay]?.bdfId?.brandId?.partyName ||
//             bookings[indexForDisplay]?.displayName
//           : null,
//       }),
//     };
//   }
// }

//   export default dateAvailability;

//   // Example usage
// //   let tempDates = [
// //     {
// //       startDate: "2024-06-01T00:00:00.000+00:00",
// //       endDate: "2024-06-18T00:00:00.000+00:00"
// //     },
// //     {
// //       startDate: "2024-06-19T00:00:00.000+00:00",
// //       endDate: "2024-07-31T00:00:00.000+00:00"
// //     },
// //       {
// //       startDate: "2024-08-01T00:00:00.000+00:00",
// //       endDate: "2024-08-09T00:00:00.000+00:00"
// //     },
// //   ];

// //   console.log(dateAvailability(tempDates));
